import type {
  ClientCareContextResponse,
  EmailItem,
  OperationAuditEvent,
  OperationCase,
  OperationMessage,
  OperationTask,
  OperationsDashboard,
  PageResponse,
  ReconciliationRun,
  RegulatoryReportRun,
  TenantRole,
  TenantUser,
} from '../api/operations-service';
import type {
  KycAmlCase,
  KycAmlClientAccount,
  KycAmlFilters,
  OpsSession,
} from './types';

export const DEMO_USERNAME = 'demo.ops@zeta.local';
export const DEMO_PASSWORD = 'Password123!';
export const DEMO_TENANT_ID = '11111111-1111-1111-1111-111111111111';
export const DEMO_ACTOR_ID = '22222222-2222-2222-2222-222222222222';

export const demoSession: OpsSession = {
  token: 'local-demo-token',
  username: DEMO_USERNAME,
  givenName: 'Demo',
  familyName: 'Admin',
  tenantId: DEMO_TENANT_ID,
  tenantCode: 'bigexchange',
  actorId: DEMO_ACTOR_ID,
  actorRole: 'ADMIN',
  actorRoles: [
    'ADMIN',
    'OPS_MANAGER',
    'CLIENT_SERVICING',
    'COMPLIANCE_OFFICER',
  ],
  isDemo: true,
};

export const demoRoles: TenantRole[] = [
  {
    roleCode: 'ADMIN',
    roleName: 'Tenant Administrator',
    description: 'Can manage tenant users and roles',
    systemRole: true,
  },
  {
    roleCode: 'OPS_MANAGER',
    roleName: 'Operations Manager',
    description: 'Can manage queues, tasks and escalations',
  },
  {
    roleCode: 'CLIENT_SERVICING',
    roleName: 'Client Servicing',
    description: 'Handles account and client service requests',
  },
  {
    roleCode: 'COMPLAINTS_HANDLER',
    roleName: 'Complaints Handler',
    description: 'Owns regulated complaint handling',
  },
  {
    roleCode: 'COMPLIANCE_USER',
    roleName: 'Compliance User',
    description: 'Reviews reports, evidence and exceptions',
  },
  {
    roleCode: 'COMPLIANCE_OFFICER',
    roleName: 'Compliance Officer',
    description: 'Can make KYC decisions and submit approval packs',
  },
  {
    roleCode: 'COMPLIANCE_SUPERVISOR',
    roleName: 'Compliance Supervisor',
    description: 'Can approve or return KYC and AML decisions',
  },
  {
    roleCode: 'SUPERVISOR',
    roleName: 'Supervisor',
    description: 'Can perform four-eyes approval decisions',
  },
];

export const demoUsers: TenantUser[] = [
  {
    userId: DEMO_ACTOR_ID,
    cognitoUsername: DEMO_USERNAME,
    username: DEMO_USERNAME,
    email: DEMO_USERNAME,
    givenName: 'Demo',
    familyName: 'Admin',
    status: 'ACTIVE',
    roleCodes: ['ADMIN', 'OPS_MANAGER'],
  },
  {
    userId: '33333333-3333-3333-3333-333333333333',
    cognitoUsername: 'servicing.ops@zeta.local',
    username: 'servicing.ops@zeta.local',
    email: 'servicing.ops@zeta.local',
    givenName: 'Maya',
    familyName: 'Shah',
    status: 'ACTIVE',
    roleCodes: ['CLIENT_SERVICING', 'CUSTOMER_CARE'],
  },
  {
    userId: '44444444-4444-4444-4444-444444444444',
    cognitoUsername: 'complaints.ops@zeta.local',
    username: 'complaints.ops@zeta.local',
    email: 'complaints.ops@zeta.local',
    givenName: 'Noah',
    familyName: 'Reed',
    status: 'INVITED',
    roleCodes: ['COMPLAINTS_HANDLER', 'COMPLIANCE_USER'],
  },
];

export const demoCases: OperationCase[] = [
  {
    caseId: '8fbf8074-8a48-4a53-a712-101e82f8ac01',
    caseReference: 'OPS-2026-1042',
    caseType: 'WITHDRAWAL_EXCEPTION',
    clientId: 'CL-100421',
    accountId: 'ISA-88201',
    customerName: 'Amara Lewis',
    customerNumber: 'CUST-55021',
    title: 'Withdrawal pending platform confirmation',
    description:
      'Platform webhook indicates withdrawal requires operations validation before release.',
    priority: 'HIGH',
    status: 'IN_PROGRESS',
    handlerType: 'HYBRID',
    assignedRole: 'CLIENT_SERVICING',
    channel: 'PHONE',
    riskLevel: 'MEDIUM',
    dueAt: demoIso(6),
    slaDueAt: demoIso(4),
    sourceService: 'zeta-investor-svc',
    sourceReferenceId: 'WD-771920',
    metadata: {
      clientContext: {
        partyId: 'CL-100421',
        clientReference: 'CUST-55021',
        legalName: 'Amara Lewis',
        email: 'amara.lewis@example.com',
        phoneNumber: '+44 7700 900421',
        status: 'ACTIVE',
        kycStatus: 'VERIFIED',
        kycVerified: true,
        kycDocumentsRequired: false,
        available: true,
        restricted: false,
        accountId: 'ISA-88201',
        accountReference: 'ISA-88201',
        accountType: 'ISA',
        wrapperType: 'ISA',
        accountStatus: 'ACTIVE',
        portfolioCurrency: 'GBP',
        accounts: [
          {
            accountId: 'ISA-88201',
            accountReference: 'ISA-88201',
            accountType: 'ISA',
            wrapperType: 'ISA',
            status: 'ACTIVE',
            cashBalance: 18420.5,
            availableToTrade: 18420.5,
            availableToWithdraw: 16620.5,
            marketValue: 126880.75,
            currencyCode: 'GBP',
            description: 'Zeta Stocks & Shares ISA',
          },
        ],
        wallets: [
          {
            walletId: 'wallet-amara-gbp',
            walletReference: 'WALLET-AMARA-GBP',
            currencyCode: 'GBP',
            availableBalance: 18420.5,
            ledgerBalance: 20220.5,
            status: 'ACTIVE',
          },
        ],
      },
      caseNotes: [
        {
          noteId: 'note-101',
          noteType: 'Decision',
          body: 'Bank details match the verified withdrawal instruction. Awaiting provider callback before release.',
          authorId: DEMO_ACTOR_ID,
          authorRole: 'ADMIN',
          createdAt: demoIso(-3),
          pinned: true,
        },
      ],
      workflowState: 'Investigation',
    },
  },
  {
    caseId: 'c2988df0-8c99-45d0-b18e-340d1fa9f4a1',
    caseReference: 'OPS-2026-1043',
    caseType: 'EMAIL_TRIAGE',
    clientId: 'CL-100433',
    accountId: 'SIPP-34210',
    customerName: 'Oliver Grant',
    customerNumber: 'CUST-55036',
    title: 'Email query about beneficiary update',
    priority: 'NORMAL',
    status: 'NEW',
    handlerType: 'HUMAN',
    assignedRole: 'OPS_AGENT',
    channel: 'EMAIL',
    riskLevel: 'LOW',
    dueAt: demoIso(24),
    sourceService: 'zeta-email-svc',
    sourceReferenceId: 'MSG-23991',
    metadata: {
      clientContext: {
        partyId: 'CL-100433',
        clientReference: 'CUST-55036',
        legalName: 'Oliver Grant',
        email: 'oliver.grant@example.com',
        phoneNumber: '+44 7700 900433',
        status: 'ACTIVE',
        kycStatus: 'PENDING',
        kycVerified: false,
        kycDocumentsRequired: true,
        available: true,
        restricted: false,
        accountId: 'SIPP-34210',
        accountReference: 'SIPP-34210',
        accountType: 'SIPP',
        wrapperType: 'SIPP',
        accountStatus: 'OPEN',
        portfolioCurrency: 'GBP',
        accounts: [
          {
            accountId: 'SIPP-34210',
            accountReference: 'SIPP-34210',
            accountType: 'SIPP',
            wrapperType: 'SIPP',
            status: 'OPEN',
            cashBalance: 0,
            marketValue: 0,
            currencyCode: 'GBP',
          },
        ],
      },
      caseNotes: [
        {
          noteId: 'note-102',
          noteType: 'Client contact',
          body: 'Client asked for beneficiary update steps by email. Send form and ID requirements.',
          authorId: '33333333-3333-3333-3333-333333333333',
          authorRole: 'OPS_AGENT',
          createdAt: demoIso(-5),
        },
      ],
      workflowState: 'Triage',
    },
  },
  {
    caseId: '9da96d45-0f4a-443d-8db1-fc5eb7e30af0',
    caseReference: 'OPS-2026-1044',
    caseType: 'COMPLAINT_REVIEW',
    clientId: 'CL-100451',
    accountId: 'GIA-29031',
    customerName: 'Priya Nair',
    title: 'Complaint detected during client contact',
    priority: 'URGENT',
    status: 'PENDING_HUMAN_REVIEW',
    handlerType: 'AI',
    assignedRole: 'COMPLAINTS_HANDLER',
    channel: 'EMAIL',
    riskLevel: 'HIGH',
    slaDueAt: demoIso(2),
    sourceService: 'zeta-operations-svc',
    metadata: {
      caseNotes: [
        {
          noteId: 'note-103',
          noteType: 'Escalation',
          body: 'Potential complaint language detected. Escalated for regulated complaint review and acknowledgement.',
          authorId: DEMO_ACTOR_ID,
          authorRole: 'ADMIN',
          createdAt: demoIso(-1),
          pinned: true,
        },
      ],
      workflowState: 'Escalated',
    },
  },
];

export const demoTasks: OperationTask[] = [
  {
    taskId: 'd8c905dd-26a1-42c7-841a-84b91b563be1',
    caseId: demoCases[0].caseId,
    taskType: 'VERIFY_WITHDRAWAL',
    title: 'Validate bank details and payment status',
    status: 'TODO',
    priority: 'HIGH',
    assignedRole: 'CLIENT_SERVICING',
    dueAt: demoIso(3),
  },
  {
    taskId: '9c0b0c80-1ae5-486a-a611-1d77d3a24042',
    caseId: demoCases[2].caseId,
    taskType: 'COMPLAINT_ACK',
    title: 'Send complaint acknowledgement',
    status: 'IN_PROGRESS',
    priority: 'URGENT',
    assignedRole: 'COMPLAINTS_HANDLER',
    dueAt: demoIso(1),
  },
  {
    taskId: 'bcb70b41-68f0-48c5-814e-b1415c7ad2df',
    caseId: demoCases[1].caseId,
    taskType: 'EMAIL_RESPONSE',
    title: 'Reply with beneficiary form requirements',
    status: 'TODO',
    priority: 'NORMAL',
    assignedRole: 'OPS_AGENT',
    dueAt: demoIso(18),
  },
];

export const demoReports: RegulatoryReportRun[] = [
  {
    runId: 'e9793c0d-5560-46df-8dbf-80d62f7b1890',
    reportType: 'CASS_RECONCILIATION',
    periodStart: '2026-05-01',
    periodEnd: '2026-05-05',
    status: 'READY_FOR_APPROVAL',
    generatedBy: 'system',
  },
  {
    runId: 'f7c2b29f-f851-4f8d-b4e2-f5b56c2fe4e3',
    reportType: 'COMPLAINTS_MI',
    periodStart: '2026-04-01',
    periodEnd: '2026-04-30',
    status: 'DRAFT',
    generatedBy: 'compliance.ops',
  },
  {
    runId: 'a75ba410-f6da-47d1-9ed0-90d3cf5a5c18',
    reportType: 'VULNERABLE_CUSTOMERS',
    periodStart: '2026-04-01',
    periodEnd: '2026-04-30',
    status: 'EXPORTED',
    generatedBy: 'system',
    exportedBy: 'compliance.ops',
  },
];

export const demoReconciliationRuns: ReconciliationRun[] = [
  {
    runId: '1f3e8f7d-5bc4-4f3d-84db-9e2c0d4fa101',
    workflowId: 'wk-eod-recon-0001',
    businessDate: '2026-07-29',
    overallStatus: 'PASSED',
    startedAt: demoIso(-2.5),
    endedAt: demoIso(-2),
    createdAt: demoIso(-2.5),
    completedAt: demoIso(-2),
    matchedCount: 3821,
    breakCount: 0,
    reportAvailable: true,
    reportStatus: 'READY',
  },
  {
    runId: '1f3e8f7d-5bc4-4f3d-84db-9e2c0d4fa102',
    workflowId: 'wk-eod-recon-0002',
    businessDate: '2026-07-28',
    overallStatus: 'COMPLETED_WITH_BREAKS',
    startedAt: demoIso(-26),
    endedAt: demoIso(-25.5),
    createdAt: demoIso(-26),
    completedAt: demoIso(-25.5),
    matchedCount: 3341,
    breakCount: 58680,
    reportAvailable: true,
    reportStatus: 'READY',
    reportLocation:
      'wk-eod-reconciliation/00000000-0000-0000-0000-000000000002/1f3e8f7d-5bc4-4f3d-84db-9e2c0d4fa102/wk-eod-reconciliation-report.csv',
  },
  {
    runId: '1f3e8f7d-5bc4-4f3d-84db-9e2c0d4fa103',
    workflowId: 'wk-eod-recon-0003',
    businessDate: '2026-07-27',
    overallStatus: 'FAILED',
    startedAt: demoIso(-50),
    endedAt: demoIso(-49.75),
    createdAt: demoIso(-50),
    completedAt: demoIso(-49.75),
    matchedCount: 0,
    breakCount: 0,
    reportAvailable: false,
    reportStatus: 'FAILED',
    failureReason: 'One ledger feed did not return within the SLA window.',
  },
];

export const demoEmailMessages: OperationMessage[] = [
  {
    messageId: 'email-101',
    caseId: demoCases[1].caseId,
    channel: 'EMAIL',
    direction: 'INBOUND',
    sender: 'oliver.grant@example.com',
    recipient: 'support@zetawealth.co.uk',
    subject: 'Beneficiary update request',
    body: 'Please send the beneficiary update form and confirm what ID you need from me.',
    receivedAt: demoIso(-6),
    externalMessageId: 'MSG-23991',
  },
  {
    messageId: 'email-102',
    caseId: demoCases[1].caseId,
    channel: 'EMAIL',
    direction: 'OUTBOUND',
    sender: 'servicing.ops@zeta.local',
    recipient: 'oliver.grant@example.com',
    subject: 'Beneficiary update request',
    body: 'We can help with that. Please complete the attached beneficiary form and provide a certified copy of ID.',
    receivedAt: demoIso(-1),
    externalMessageId: 'MSG-23991-R1',
  },
  {
    messageId: 'email-103',
    caseId: demoCases[0].caseId,
    channel: 'EMAIL',
    direction: 'OUTBOUND',
    sender: 'demo.ops@zeta.local',
    recipient: 'amara.lewis@example.com',
    subject: 'Withdrawal update',
    body: 'We are waiting for platform confirmation and will update you when the payment is released.',
    receivedAt: demoIso(-0.5),
    externalMessageId: 'WD-771920-EMAIL',
  },
];

export const demoEmailItems: EmailItem[] = demoEmailMessages.map((message) => ({
  message,
  caseItem: demoCases.find((item) => item.caseId === message.caseId),
}));

export const demoDocuments = [
  {
    documentRequestId: 'doc-101',
    requestedDocumentType: 'Bank statement',
    reason: 'Payment confirmation evidence',
    status: 'REQUESTED',
    dueAt: demoIso(18),
  },
  {
    documentRequestId: 'doc-102',
    requestedDocumentType: 'Beneficiary form',
    reason: 'Beneficiary update request',
    status: 'RECEIVED',
    dueAt: demoIso(-12),
  },
];

export const demoClientCareContext: ClientCareContextResponse = {
  lookup: {
    clientId: 'CL-100421',
    accountId: 'ISA-88201',
    tradeReference: 'WD-771920',
  },
  clientSnapshot: {
    clientId: 'CL-100421',
    accountId: 'ISA-88201',
    profile: {
      fullName: 'Amara Lewis',
      email: 'amara.lewis@example.com',
      mobile: '+44 7700 900421',
    },
    party: {
      dateOfBirth: '1984-02-15',
      nationalInsuranceNumber: 'QQ123456C',
      addressLine1: '14 Queen Street',
      city: 'London',
    },
    account: {
      accountId: 'ISA-88201',
      accountNumber: 'ISA-88201',
      accountType: 'Stocks & Shares ISA',
      status: 'ACTIVE',
      openedAt: '2023-04-06',
      productName: 'Zeta ISA',
    },
    holdings: [
      {
        accountId: 'ISA-88201',
        items: [
          {
            isin: 'GB00B7N0K053',
            fundName: 'UK Equity Income Fund',
            status: 'SETTLED',
            settledUnits: 842.5123,
            settledPrice: 12.47,
            totalValue: 10508.13,
          },
          {
            isin: 'GB00BD3RZ582',
            fundName: 'Global Sustainable Growth Fund',
            status: 'SETTLED',
            settledUnits: 1240.75,
            settledPrice: 18.92,
            totalValue: 23475,
          },
        ],
      },
    ],
    portfolio: {
      valuation: 126880.75,
      currency: 'GBP',
      performanceYtd: 0.071,
      holdings: 12,
    },
    kycAmlScreening: {
      summary: {
        screeningStatus: 'VERIFIED',
        kycVerified: true,
        reviewRequired: false,
      },
    },
    cashPosition: {
      availableCash: 18420.5,
      settledCash: 20220.5,
      pendingCash: -1800,
      currency: 'GBP',
    },
    orders: {
      items: [
        {
          orderReference: 'TRD-22091',
          instrumentName: 'UK Equity Income Fund',
          side: 'BUY',
          status: 'SETTLED',
          amount: 12500,
          currency: 'GBP',
          createdAt: demoIso(-72),
        },
      ],
    },
    deposits: {
      items: [
        {
          reference: 'DEP-2388',
          status: 'COMPLETED',
          amount: 5000,
          currency: 'GBP',
          receivedAt: demoIso(-48),
        },
      ],
    },
    withdrawals: {
      items: [
        {
          reference: 'WD-771920',
          status: 'PENDING_REVIEW',
          amount: 3200,
          currency: 'GBP',
          requestedAt: demoIso(-6),
        },
      ],
    },
    transactions: {
      items: [
        {
          reference: 'TX-11002',
          type: 'WITHDRAWAL',
          amount: 3200,
          currency: 'GBP',
          status: 'PENDING',
          transactionDate: demoIso(-5),
        },
        {
          reference: 'TX-10980',
          type: 'DIVIDEND',
          amount: 142.36,
          currency: 'GBP',
          status: 'SETTLED',
          transactionDate: demoIso(-120),
        },
      ],
    },
    transfers: {
      items: [
        {
          transferReference: 'TRF-7781',
          providerName: 'Previous Platform',
          transferType: 'Cash transfer',
          status: 'IN_PROGRESS',
          amount: 18000,
          currency: 'GBP',
          createdAt: demoIso(-96),
        },
      ],
    },
    documents: {
      items: [
        {
          reference: 'DOC-881',
          documentType: 'Bank statement',
          status: 'RECEIVED',
          createdAt: demoIso(-24),
        },
      ],
    },
  },
  cases: demoCases,
  serviceRequests: [
    {
      requestId: 'svc-101',
      caseId: demoCases[0].caseId,
      clientId: 'CL-100421',
      accountId: 'ISA-88201',
      requestType: 'WITHDRAWAL_SUPPORT',
      channel: 'PHONE',
      subject: 'Withdrawal status call',
      status: 'OPEN',
      assignedRole: 'CLIENT_SERVICING',
      dueAt: demoIso(3),
    },
  ],
  documentRequests: demoDocuments,
};

export const demoAuditEvents: OperationAuditEvent[] = [
  {
    auditId: 'audit-101',
    entityType: 'OperationCase',
    entityId: demoCases[0].caseId,
    action: 'CREATED',
    actorId: 'system',
    actorRole: 'SYSTEM',
    message: 'Case created from platform withdrawal webhook',
    createdAt: demoIso(-6),
  },
  {
    auditId: 'audit-102',
    entityType: 'OperationCase',
    entityId: demoCases[0].caseId,
    action: 'UPDATED',
    actorId: DEMO_ACTOR_ID,
    actorRole: 'ADMIN',
    message: 'Priority changed to HIGH and assigned to Client Servicing',
    createdAt: demoIso(-4),
  },
  {
    auditId: 'audit-103',
    entityType: 'OperationCase',
    entityId: demoCases[2].caseId,
    action: 'ESCALATED',
    actorId: DEMO_ACTOR_ID,
    actorRole: 'ADMIN',
    message: 'Complaint review escalation created',
    createdAt: demoIso(-1),
  },
];

export const demoAiTrace = {
  events: [
    {
      eventType: 'TRIAGE',
      agentName: 'OperationsTriageAgent',
      outcome: 'HUMAN_REVIEW',
      confidence: 0.78,
      createdAt: demoIso(-6),
    },
    {
      eventType: 'GUARDRAIL',
      agentName: 'OperationsReviewAgents',
      outcome: 'PASSED',
      confidence: 0.93,
      createdAt: demoIso(-5),
    },
  ],
};

export const demoDashboard: OperationsDashboard = {
  openCasesByRole: { CLIENT_SERVICING: 1, OPS_AGENT: 1, COMPLAINTS_HANDLER: 1 },
  openCasesByStatus: { IN_PROGRESS: 1, NEW: 1, PENDING_HUMAN_REVIEW: 1 },
  openCasesByPriority: { URGENT: 1, HIGH: 1, NORMAL: 1 },
  openTasksByRole: { CLIENT_SERVICING: 1, OPS_AGENT: 1, COMPLAINTS_HANDLER: 1 },
  openTasksByStatus: { TODO: 2, IN_PROGRESS: 1 },
  openTasksByPriority: { URGENT: 1, HIGH: 1, NORMAL: 1 },
  priorityCases: 2,
  slaBreachedCases: 0,
  dueSoonCases: 2,
  priorityTasks: 2,
  overdueTasks: 0,
  readyReports: 1,
  openDocumentRequests: 1,
};

export const demoKycAmlCases: KycAmlCase[] = [
  {
    caseId: '50b1c3c8-36c6-454a-9ccf-42d0ca65a001',
    caseReference: 'KYC-2026-7712',
    clientId: 'a3c9b548-bf5a-4a92-9a52-4c93d4a4b7a0',
    clientName: 'Amara Lewis',
    clientNumber: 'CUST-55021',
    accountId: 'ISA-88201',
    reviewType: 'Onboarding due diligence',
    riskLevel: 'MEDIUM',
    kycStatus: 'PENDING_APPROVAL',
    amlStatus: 'CLEAR',
    onboardingStatus: 'READY_FOR_DECISION',
    dueDiligenceStatus: 'ENHANCED_DUE_DILIGENCE',
    workflowStage: 'Four-eyes review',
    approvalStage: 'Supervisor approval',
    assignedTo: 'Maya Shah',
    assignedRole: 'COMPLIANCE_OFFICER',
    dueAt: demoIso(6),
    lastUpdatedAt: demoIso(-1),
    handlerType: 'HYBRID',
    flags: ['High deposit velocity', 'Manual source-of-funds override'],
    notes:
      'Evidence is complete. Source-of-funds override requires supervisor approval before account activation.',
    checks: [
      {
        checkId: 'chk-1001',
        name: 'Identity verification',
        category: 'Onboarding',
        status: 'COMPLETED',
        result: 'PASS',
        mode: 'AI',
        confidence: 0.96,
        lastRunAt: demoIso(-14),
        detail: 'ID document and selfie match.',
      },
      {
        checkId: 'chk-1002',
        name: 'Address verification',
        category: 'Onboarding',
        status: 'COMPLETED',
        result: 'PASS',
        mode: 'AI',
        confidence: 0.91,
        lastRunAt: demoIso(-13),
        detail: 'Address evidence accepted.',
      },
      {
        checkId: 'chk-1003',
        name: 'Sanctions and PEP',
        category: 'Screening',
        status: 'COMPLETED',
        result: 'CLEAR',
        mode: 'AI',
        confidence: 0.98,
        lastRunAt: demoIso(-8),
        detail: 'No sanctions or PEP match.',
      },
      {
        checkId: 'chk-1004',
        name: 'Adverse media',
        category: 'Screening',
        status: 'REVIEWED',
        result: 'NO_MATERIAL_MATCH',
        mode: 'Hybrid',
        confidence: 0.82,
        lastRunAt: demoIso(-7),
        detail: 'False positive dismissed by compliance.',
      },
      {
        checkId: 'chk-1005',
        name: 'Source of funds',
        category: 'Due diligence',
        status: 'PENDING_APPROVAL',
        result: 'OVERRIDE_REQUESTED',
        mode: 'Manual',
        lastRunAt: demoIso(-4),
        detail: 'Payslip and bank statement support the first subscription.',
      },
      {
        checkId: 'chk-1006',
        name: 'Tax residency',
        category: 'Due diligence',
        status: 'COMPLETED',
        result: 'PASS',
        mode: 'AI',
        confidence: 0.89,
        lastRunAt: demoIso(-12),
        detail: 'UK tax residency confirmed.',
      },
    ],
    documents: [
      {
        documentId: 'doc-9001',
        documentType: 'Passport',
        status: 'RECEIVED',
        reviewStatus: 'ACCEPTED',
        required: true,
        reviewer: 'AI document review',
        uploadedAt: demoIso(-15),
      },
      {
        documentId: 'doc-9002',
        documentType: 'Bank statement',
        status: 'RECEIVED',
        reviewStatus: 'MANUAL_REVIEWED',
        required: true,
        reviewer: 'Maya Shah',
        uploadedAt: demoIso(-5),
      },
      {
        documentId: 'doc-9003',
        documentType: 'Payslip',
        status: 'RECEIVED',
        reviewStatus: 'PENDING_SUPERVISOR',
        required: true,
        reviewer: 'Maya Shah',
        uploadedAt: demoIso(-5),
        dueAt: demoIso(5),
      },
    ],
    overrides: [
      {
        overrideId: 'ovr-7001',
        type: 'Source of funds',
        status: 'PENDING_SUPERVISOR',
        requestedBy: 'Maya Shah',
        requestedAt: demoIso(-3),
        reason:
          'Evidence supports initial subscription despite AI velocity flag.',
        decision: 'Approve onboarding with 90-day monitoring.',
      },
    ],
    accountBlocks: [
      {
        blockId: 'blk-501',
        accountId: 'ISA-88201',
        blockType: 'Trading and withdrawal hold',
        status: 'ACTIVE',
        source: 'AI',
        reason: 'Prevent activity until due diligence is approved.',
        createdAt: demoIso(-10),
      },
    ],
  },
  {
    caseId: '50b1c3c8-36c6-454a-9ccf-42d0ca65a002',
    caseReference: 'AML-2026-3309',
    clientId: '66f3e641-8cde-41f5-96e7-319ddc9017c1',
    clientName: 'Priya Nair',
    clientNumber: 'CUST-55051',
    accountId: 'GIA-29031',
    reviewType: 'Transaction monitoring',
    riskLevel: 'HIGH',
    kycStatus: 'VERIFIED',
    amlStatus: 'INVESTIGATION',
    onboardingStatus: 'ACTIVE_CLIENT',
    dueDiligenceStatus: 'ONGOING_MONITORING',
    workflowStage: 'Analyst review',
    approvalStage: 'Compliance officer decision',
    assignedTo: 'Noah Reed',
    assignedRole: 'COMPLIANCE_USER',
    dueAt: demoIso(2),
    lastUpdatedAt: demoIso(-0.5),
    handlerType: 'AI',
    flags: ['Unusual withdrawal pattern', 'Adverse media false positive'],
    notes:
      'AML alert created from transaction-monitoring rules. Analyst must document rationale before submitting.',
    checks: [
      {
        checkId: 'chk-2001',
        name: 'Transaction pattern',
        category: 'AML',
        status: 'REVIEW_REQUIRED',
        result: 'ALERT',
        mode: 'AI',
        confidence: 0.88,
        lastRunAt: demoIso(-2),
        detail: 'Three same-day withdrawals above normal profile.',
      },
      {
        checkId: 'chk-2002',
        name: 'Sanctions refresh',
        category: 'Screening',
        status: 'COMPLETED',
        result: 'CLEAR',
        mode: 'AI',
        confidence: 0.99,
        lastRunAt: demoIso(-2),
        detail: 'No current sanctions match.',
      },
      {
        checkId: 'chk-2003',
        name: 'Adverse media refresh',
        category: 'Screening',
        status: 'REVIEWED',
        result: 'FALSE_POSITIVE',
        mode: 'Manual',
        lastRunAt: demoIso(-1.5),
        detail: 'Name match unrelated to client.',
      },
      {
        checkId: 'chk-2004',
        name: 'Account purpose',
        category: 'Due diligence',
        status: 'COMPLETED',
        result: 'CONSISTENT',
        mode: 'Hybrid',
        confidence: 0.78,
        lastRunAt: demoIso(-20),
        detail:
          'Activity broadly aligns to investment profile except withdrawals.',
      },
    ],
    documents: [
      {
        documentId: 'doc-9101',
        documentType: 'Withdrawal evidence',
        status: 'REQUESTED',
        reviewStatus: 'WAITING_CLIENT',
        required: true,
        dueAt: demoIso(20),
      },
      {
        documentId: 'doc-9102',
        documentType: 'Risk review worksheet',
        status: 'DRAFT',
        reviewStatus: 'ANALYST_DRAFT',
        reviewer: 'Noah Reed',
      },
    ],
    overrides: [],
    accountBlocks: [
      {
        blockId: 'blk-601',
        accountId: 'GIA-29031',
        blockType: 'Withdrawal block',
        status: 'ACTIVE',
        source: 'Manual',
        reason: 'Operations hold while AML alert is reviewed.',
        createdAt: demoIso(-2),
      },
    ],
  },
  {
    caseId: '50b1c3c8-36c6-454a-9ccf-42d0ca65a003',
    caseReference: 'KYC-2026-7718',
    clientId: '1ecf0962-11d2-4283-b94b-1a168c85c4e4',
    clientName: 'Oliver Grant',
    clientNumber: 'CUST-55036',
    accountId: 'SIPP-34210',
    reviewType: 'KYC refresh',
    riskLevel: 'LOW',
    kycStatus: 'REQUEST_INFO',
    amlStatus: 'CLEAR',
    onboardingStatus: 'EXISTING_CLIENT_REVIEW',
    dueDiligenceStatus: 'STANDARD_DUE_DILIGENCE',
    workflowStage: 'Document collection',
    approvalStage: 'Analyst review',
    assignedTo: 'Maya Shah',
    assignedRole: 'COMPLIANCE_USER',
    dueAt: demoIso(28),
    lastUpdatedAt: demoIso(-6),
    handlerType: 'Manual',
    flags: ['Expired address evidence'],
    notes:
      'Client needs a current proof of address before review can move to decision.',
    checks: [
      {
        checkId: 'chk-3001',
        name: 'Identity refresh',
        category: 'Onboarding',
        status: 'COMPLETED',
        result: 'PASS',
        mode: 'AI',
        confidence: 0.94,
        lastRunAt: demoIso(-10),
        detail: 'Identity evidence remains valid.',
      },
      {
        checkId: 'chk-3002',
        name: 'Address refresh',
        category: 'Onboarding',
        status: 'PENDING_CLIENT',
        result: 'DOCUMENT_REQUIRED',
        mode: 'Manual',
        lastRunAt: demoIso(-6),
        detail: 'Current proof of address is required.',
      },
      {
        checkId: 'chk-3003',
        name: 'Sanctions and PEP refresh',
        category: 'Screening',
        status: 'COMPLETED',
        result: 'CLEAR',
        mode: 'AI',
        confidence: 0.98,
        lastRunAt: demoIso(-6),
        detail: 'No current screening hit.',
      },
    ],
    documents: [
      {
        documentId: 'doc-9201',
        documentType: 'Proof of address',
        status: 'REQUESTED',
        reviewStatus: 'WAITING_CLIENT',
        required: true,
        dueAt: demoIso(28),
      },
    ],
    overrides: [],
    accountBlocks: [],
  },
];

export function isDemoLogin(username: string, password: string): boolean {
  const demoEnabled =
    import.meta.env.DEV || import.meta.env.VITE_ENABLE_OPS_DEMO === 'true';
  return (
    demoEnabled &&
    username.trim().toLowerCase() === DEMO_USERNAME &&
    password === DEMO_PASSWORD
  );
}

export function demoIso(offsetHours: number): string {
  return new Date(Date.now() + offsetHours * 60 * 60 * 1000).toISOString();
}

export function demoPage<T>(
  items: T[],
  pageSize: number,
  total = items.length,
  pageIndex = 0,
): PageResponse<T> {
  return {
    items,
    total,
    pageIndex,
    pageSize,
  };
}

export function demoFilteredCases(filters: {
  q: string;
  status: string;
  role: string;
}): OperationCase[] {
  const query = filters.q.trim().toLowerCase();
  return demoCases.filter((item) => {
    const statusMatches =
      !filters.status ||
      (filters.status === 'OPEN_CASES' &&
        !demoTerminalCaseStatus(item.status)) ||
      (filters.status === 'CLOSED_CASES' &&
        demoTerminalCaseStatus(item.status)) ||
      item.status === filters.status;
    const roleMatches = !filters.role || item.assignedRole === filters.role;
    const queryMatches =
      !query ||
      [
        item.caseReference,
        item.title,
        item.customerName,
        item.clientId,
        item.accountId,
        item.customerNumber,
        item.sourceReferenceId,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query);
    return statusMatches && roleMatches && queryMatches;
  });
}

function demoTerminalCaseStatus(value?: string): boolean {
  return ['CLOSED', 'COMPLETED', 'CANCELLED', 'RESOLVED', 'FAILED'].includes(
    (value ?? '').toUpperCase(),
  );
}

export function demoFilteredKycAmlCases(filters: KycAmlFilters): KycAmlCase[] {
  const query = filters.q.trim().toLowerCase();
  return demoKycAmlCases.filter((item) => {
    const statusMatches =
      !filters.status ||
      item.kycStatus === filters.status ||
      item.approvalStage.toUpperCase().replace(/\s+/g, '_') === filters.status;
    const riskMatches =
      !filters.riskLevel || item.riskLevel === filters.riskLevel;
    const workflowMatches =
      !filters.workflowStage || item.workflowStage === filters.workflowStage;
    const queryMatches =
      !query ||
      [
        item.caseReference,
        item.clientName,
        item.clientId,
        item.clientNumber,
        item.accountId,
        item.reviewType,
        item.flags?.join(' '),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query);
    return statusMatches && riskMatches && workflowMatches && queryMatches;
  });
}

export function demoAccountsForKycCase(
  caseItem: KycAmlCase,
): KycAmlClientAccount[] {
  if (caseItem.accounts?.length) {
    return caseItem.accounts;
  }
  const blocked = caseItem.accountBlocks.some(
    (block) => block.status === 'ACTIVE',
  );
  return [
    {
      accountId: caseItem.accountId || caseItem.clientId,
      partyId: caseItem.clientId,
      accountReference: caseItem.accountId,
      accountType: caseItem.accountId?.startsWith('SIPP')
        ? 'SIPP'
        : caseItem.accountId?.startsWith('GIA')
          ? 'GIA'
          : 'ISA',
      wrapperType: caseItem.accountId?.startsWith('SIPP')
        ? 'SIPP'
        : caseItem.accountId?.startsWith('GIA')
          ? 'GIA'
          : 'ISA',
      status: blocked ? 'SUSPENDED' : 'ACTIVE',
      openedAt: demoIso(-9000),
      cashBalance: caseItem.riskLevel === 'HIGH' ? 4260 : 18420,
      currency: 'GBP',
    },
  ];
}

export function demoFilteredEmailItems(filters: {
  q: string;
  direction: string;
  clientId: string;
  accountId: string;
}): EmailItem[] {
  const query = filters.q.trim().toLowerCase();
  return demoEmailItems.filter((item) => {
    const message = item.message;
    const caseItem = item.caseItem;
    const directionMatches =
      !filters.direction || message?.direction === filters.direction;
    const clientMatches =
      !filters.clientId || caseItem?.clientId === filters.clientId;
    const accountMatches =
      !filters.accountId || caseItem?.accountId === filters.accountId;
    const queryMatches =
      !query ||
      [
        message?.sender,
        message?.recipient,
        message?.subject,
        message?.body,
        message?.externalMessageId,
        caseItem?.caseReference,
        caseItem?.customerName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query);
    return directionMatches && clientMatches && accountMatches && queryMatches;
  });
}
